/**
 * @file api-site-view.ts
 * @description Клиент `admin/site-view/*` — «вид сайта» магазина (phase19.16,
 * релиз Р1): черновик по частям, меню, «что изменено», публикация, отмена,
 * история с откатом, токен предпросмотра, свои шрифты магазина (Р1-7, SB99;
 * multipart через `apiFetch({ form })`) (контракт
 * `docs/phases/phase19.16-materials/site-view-api-contract.md` §1–§7).
 *
 * Р3г (db-design-r3g §4): часть COUNTERS — `GET/PATCH draft/counters`;
 * переход в режим «Уведомление» несёт `noticeAck { version }` — галочку окна
 * о риске (SB133) с номером ТОЙ редакции текста окна, которую сотрудник
 * видел (`consentPlatform.noticeRisk.currentVersion`, без поля — встроенная 1);
 * устарела — 422 `site_counters_notice_ack_required` (docs/api/site-counters.md).
 *
 * Все правки части несут `expectedVersion` — версию, прочитанную вместе с
 * частью; несовпадение → 409 `site_view_version_conflict` (разбор —
 * `site-view/errors.ts`). Ответы `{ data }` / `{ error }` разбирает
 * `apiFetch`: на ошибке летит `ApiError` с кодом и `details`.
 */

import type { CatalogBadgeKey, SiteBlogOptions, SiteCatalogOptions, SiteCounters, SiteNavigationOptions, SiteTheme, SiteViewPart } from "@chaiberi/shared";

import { apiFetch } from "./api-client";
import { invalidateLiveSiteIssues, type SiteLiveIssues } from "./site-view/live-issues-store";
import {
  type FontInspectResult,
  type ShopFontCategoryDto,
  type ShopFontDeleteResult,
  type ShopFontDto,
  type ShopFontList,
  type ShopFontStyleDto,
  type ShopFontUploadResult,
} from "./site-view/shop-fonts";
import type {
  SiteConsentPlatform,
  SiteBlogDraft,
  SiteBrandDraft,
  SiteCatalogDraft,
  SiteHistoryPage,
  SiteHistoryVersion,
  SiteMenuItem,
  SiteMenuItemInput,
  SiteMenusResponse,
  SitePartState,
  SitePreviewSource,
  SitePreviewToken,
  SitePublishResult,
  SiteRollbackPreview,
  SiteRollbackResult,
  SiteViewChanges,
  SiteViewDraft,
} from "./site-view/types";

const BASE = "/admin/site-view";

/** Опубликованный сайт сменился — плашка «Сайт работает без…» перечитается (live-issues-store). */
function afterLiveChange<T>(result: T): T {
  invalidateLiveSiteIssues();
  return result;
}

/** Тело правки BRAND: медиа — id своего магазина, `null` очищает. */
export interface SiteBrandPatch {
  siteName?: string;
  tagline?: string | null;
  logoLightMediaId?: string | null;
  logoDarkMediaId?: string | null;
  faviconMediaId?: string | null;
  contacts?: SiteBrandDraft["contacts"];
  footerSignature?: string | null;
  footerDecorMediaId?: string | null;
  copyrightText?: string | null;
  workingHours?: string | null;
  deliveryTermsPageId?: string | null;
}

/** Тело правки BLOG. */
export interface SiteBlogPatch {
  eyebrow?: string | null;
  title?: string | null;
  subtitle?: string | null;
  coverMediaId?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  rssTitle?: string | null;
  rssDescription?: string | null;
  /** §16.8: вид ленты и статьи — сливается с текущим по полям. */
  options?: Partial<SiteBlogOptions>;
}

/**
 * Тело правки CATALOG (§19.1): параметры сливаются с текущими по полям на
 * любой глубине, массивы заменяются целиком; `badgeLabels.X: null` — убрать
 * свою подпись; `options: null` — умолчания платформы.
 */
export interface SiteCatalogPatch {
  options: (Omit<Partial<SiteCatalogOptions>, "badgeLabels"> & { badgeLabels?: Partial<Record<CatalogBadgeKey, string | null>> }) | null;
}

/**
 * Тело правки COUNTERS (Р3г): часть целиком. `noticeAck` — галочка «отвечаю
 * сам» окна о риске режима «Уведомление» с номером показанной редакции его
 * текста; сервер пишет её в журнал действий.
 */
export interface SiteCountersPatch {
  counters: SiteCounters;
  noticeAck?: { version: number };
}

type Versioned<T> = T & { expectedVersion: number };

/** Ответы чтения и правки частей (`{ part, <часть> }`). */
export interface ThemePartResponse {
  part: SitePartState;
  theme: SiteTheme;
}
export interface BrandPartResponse {
  part: SitePartState;
  brand: SiteBrandDraft;
}
export interface BlogPartResponse {
  part: SitePartState;
  blog: SiteBlogDraft;
}
export interface CatalogPartResponse {
  part: SitePartState;
  catalog: SiteCatalogDraft;
}
/**
 * «SEO-техника» v0.19.65 (SB146; db-design-seo-tech §1.4): документ
 * навигации части MENUS — ответ `PATCH /admin/site-view/navigation`.
 */
export interface NavigationPartResponse {
  part: SitePartState;
  navigation: SiteNavigationOptions;
}

/** Р3г: часть COUNTERS и политика платформы по согласию. */
export interface CountersPartResponse {
  part: SitePartState;
  counters: SiteCounters;
  consentPlatform?: SiteConsentPlatform;
}

export const siteView = {
  /** Весь черновик одним ответом (кроме меню). */
  draft: () => apiFetch<SiteViewDraft>(`${BASE}/draft`),

  theme: () => apiFetch<ThemePartResponse>(`${BASE}/draft/theme`),
  brand: () => apiFetch<BrandPartResponse>(`${BASE}/draft/brand`),
  blog: () => apiFetch<BlogPartResponse>(`${BASE}/draft/blog`),
  catalog: () => apiFetch<CatalogPartResponse>(`${BASE}/draft/catalog`),
  counters: () => apiFetch<CountersPartResponse>(`${BASE}/draft/counters`),

  /** Документ оформления отправляется целиком (контракт §1). */
  saveTheme: (expectedVersion: number, theme: SiteTheme) =>
    apiFetch<ThemePartResponse>(`${BASE}/draft/theme`, {
      method: "PATCH",
      json: { expectedVersion, theme },
    }),
  saveBrand: (body: Versioned<SiteBrandPatch>) =>
    apiFetch<BrandPartResponse>(`${BASE}/draft/brand`, { method: "PATCH", json: body }),
  // Р3в (SB145): часть SEO вида сайта больше не правится — `PATCH draft/seo` → 410 `site_seo_moved`,
  // общие SEO-настройки — `api-site-seo.ts` («Сайт → SEO»).
  saveBlog: (body: Versioned<SiteBlogPatch>) =>
    apiFetch<BlogPartResponse>(`${BASE}/draft/blog`, { method: "PATCH", json: body }),
  saveCatalog: (body: Versioned<SiteCatalogPatch>) =>
    apiFetch<CatalogPartResponse>(`${BASE}/draft/catalog`, { method: "PATCH", json: body }),
  saveCounters: (body: Versioned<SiteCountersPatch>) =>
    apiFetch<CountersPartResponse>(`${BASE}/draft/counters`, { method: "PATCH", json: body }),

  /**
   * «SEO-техника» v0.19.65 (SB146): «Показывать хлебные крошки» — документ
   * навигации части MENUS; версия — общая с пунктами меню (`expectedVersion`
   * части MENUS), правка попадает в ●N, «Отменить изменения» и публикацию.
   */
  saveNavigation: (body: Versioned<SiteNavigationOptions>) =>
    apiFetch<NavigationPartResponse>(`${BASE}/navigation`, { method: "PATCH", json: body }),

  // ── Меню (контракт §2) ──
  menus: () => apiFetch<SiteMenusResponse>(`${BASE}/menus`),
  createMenuItem: (
    body: Versioned<SiteMenuItemInput & { location: "HEADER" | "FOOTER"; parentId: string | null; index?: number }>,
  ) => apiFetch<{ part: SitePartState; item: SiteMenuItem }>(`${BASE}/menus/items`, { method: "POST", json: body }),
  updateMenuItem: (id: string, body: Versioned<Partial<SiteMenuItemInput>>) =>
    apiFetch<{ part: SitePartState; item: SiteMenuItem }>(`${BASE}/menus/items/${id}`, {
      method: "PATCH",
      json: body,
    }),
  deleteMenuItem: (id: string, expectedVersion: number) =>
    apiFetch<{ part: SitePartState }>(`${BASE}/menus/items/${id}?expectedVersion=${expectedVersion}`, {
      method: "DELETE",
    }),
  reorderMenu: (body: Versioned<{ location: "HEADER" | "FOOTER"; items: Array<{ id: string; parentId: string | null }> }>) =>
    apiFetch<SiteMenusResponse>(`${BASE}/menus/order`, { method: "PUT", json: body }),

  // ── Публикация (контракт §3–§5) ──
  changes: () => apiFetch<SiteViewChanges>(`${BASE}/changes`),
  /** SB125: `acknowledgeChecks` — публикую с незакрытыми проверками (право SHOP_MANAGE_SETTINGS). */
  publish: (fingerprint: string, options: { acknowledgeChecks?: boolean } = {}) =>
    apiFetch<SitePublishResult>(`${BASE}/publish`, {
      method: "POST",
      json: options.acknowledgeChecks ? { fingerprint, acknowledgeChecks: true } : { fingerprint },
    }).then(afterLiveChange),
  /**
   * SB120 этап 2 (db-design-sb120-stage2 §6): без чего опубликованный сайт
   * работает сейчас — ошибки по данным магазина; право — смотреть черновик
   * вида сайта (иначе 403). Не опубликован — `published: false`, `errors: []`.
   */
  liveIssues: () => apiFetch<SiteLiveIssues>(`${BASE}/live-issues`),
  discard: (parts: SiteViewPart[]) =>
    apiFetch<SiteViewDraft>(`${BASE}/discard`, { method: "POST", json: { parts } }),

  // ── История (контракт §6) ──
  history: (cursor?: string | null, limit = 20) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (cursor) q.set("cursor", cursor);
    return apiFetch<SiteHistoryPage>(`${BASE}/history?${q.toString()}`);
  },
  historyVersion: (version: number) => apiFetch<SiteHistoryVersion>(`${BASE}/history/${version}`),
  rollbackPreview: (version: number) =>
    apiFetch<SiteRollbackPreview>(`${BASE}/history/${version}/rollback-preview`),
  rollback: (version: number) =>
    apiFetch<SiteRollbackResult>(`${BASE}/history/${version}/rollback`, { method: "POST" }).then(afterLiveChange),

  // ── Предпросмотр (контракт §7) ──
  /** `path` — адрес своего сайта, куда войти (`/preview/<slug>` — канва страницы, §14.1). */
  previewToken: (source: SitePreviewSource, path?: string) =>
    apiFetch<SitePreviewToken>(`${BASE}/preview-token`, {
      method: "POST",
      json: path ? { ...source, path } : source,
    }),
};

/** Поля загрузки нового шрифта: файлы по местам (`regular` обязателен). */
export interface CreateShopFontInput {
  family: string;
  category: ShopFontCategoryDto;
  sourceNote: string | null;
  /** Имя поля multipart → файл: `regular`, `bold`, `italic`, `boldItalic`. */
  files: Record<string, File>;
}

/** Догрузка начертания к своему шрифту. */
export interface AddShopFontFileInput {
  file: File;
  weight: 400 | 700;
  style: ShopFontStyleDto;
}

/**
 * Свои шрифты магазина (Р1-7, SB99; право `SHOP_MANAGE_SETTINGS`). Ошибки:
 * 409 font_limit / font_family_taken / font_file_exists / font_in_use,
 * 422 font_rights_required / font_file_invalid / font_file_format /
 * font_file_required, 404 font_not_found
 * (тексты — `site-view/shop-font-errors.ts`).
 */
export const siteFonts = {
  list: (): Promise<ShopFontList> =>
    apiFetch<ShopFontList>(`${BASE}/fonts`),

  /** Проверка файла сразу при выборе — сервер ничего не сохраняет. */
  inspect: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiFetch<FontInspectResult>(`${BASE}/fonts/inspect`, { method: "POST", form });
  },

  /** Право на шрифт подтверждено галочкой окна — без неё кнопка выключена. */
  create: (input: CreateShopFontInput) => {
    const form = new FormData();
    form.append("family", input.family);
    form.append("category", input.category);
    if (input.sourceNote) form.append("sourceNote", input.sourceNote);
    form.append("rightsConfirmed", "true");
    for (const [field, file] of Object.entries(input.files)) form.append(field, file);
    return apiFetch<ShopFontUploadResult>(`${BASE}/fonts`, { method: "POST", form });
  },

  addFile: (id: string, input: AddShopFontFileInput) => {
    const form = new FormData();
    form.append("file", input.file);
    form.append("weight", String(input.weight));
    form.append("style", input.style);
    form.append("rightsConfirmed", "true");
    return apiFetch<ShopFontUploadResult>(`${BASE}/fonts/${id}/files`, { method: "POST", form });
  },

  update: (id: string, patch: { family?: string; category?: ShopFontCategoryDto; sourceNote?: string | null }) =>
    apiFetch<ShopFontDto>(`${BASE}/fonts/${id}`, { method: "PATCH", json: patch }),

  remove: (id: string) => apiFetch<ShopFontDeleteResult>(`${BASE}/fonts/${id}`, { method: "DELETE" }),
};
